import {
  FormControlLabel,
  FormGroup,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import KeyIn from "../components/dashboard/KeyIn";
import Support from "../components/dashboard/Support";
import SupportResult from "../components/dashboard/SupportResult";
import TaskCreator from "../components/dashboard/TaskCreator";
import TopKeyinReject from "../components/dashboard/TopKeyinReject";
import TopTaskCreateReject from "../components/dashboard/TopTaskCreateReject";
import ToQCAnswer from "../components/dashboard/ToQCAnswer";
import TotalTaskToday from "../components/dashboard/TotalTaskToday";
import UrgentTask from "../components/dashboard/UrgentTask";
import UserCalled from "../components/dashboard/UserCalled";
import useDashboard from "../hooks/useDashboard";
import useOnlineUser from "../hooks/useOnlineUser";
import { UserOnlineData } from "../types/user";
import { channelFilterList } from "../utils/constants";

type GroupedUsers = {
  current_page: string;
  users: { name: string }[];
};

export default function Dashboard() {
  const [isTask, setIsTask] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [filter, setFilter] = useState<any>({});
  const [pageGroupData, setPageGroupData] = useState<GroupedUsers[]>([]);

  const { data: onlineUsers, dataLoading: onlineUsersLoading } =
    useOnlineUser(true);
  const { data, dataLoading } = useDashboard(true, {
    ...filter,
  });

  useEffect(() => {
    return () => setSelectedChannel("");
  }, []);

  const titleOverview = useMemo(() => {
    return `${isTask ? "Task" : "Job"}`;
  }, [isTask]);

  const onChangeChannel = (value: string) => {
    setSelectedChannel(value);
    if (value) {
      setFilter({
        channel: value,
      });
    } else {
      setFilter({});
    }
  };

  const transformUsers = (data: UserOnlineData): GroupedUsers[] => {
    if (!data?.online_users) {
      return [];
    }

    const grouped = data?.online_users.reduce<
      Record<string, { name: string }[]>
    >((acc, user) => {
      const page = user.current_page || ""; // fallback if empty
      if (!acc[page]) {
        acc[page] = [];
      }
      acc[page].push({ name: user.name });
      return acc;
    }, {});

    return Object.entries(grouped).map(([page, users]) => ({
      current_page: page,
      users,
    }));
  };

  useEffect(() => {
    if (onlineUsers?.online_users) {
      const groupedData = transformUsers(onlineUsers);
      setPageGroupData(groupedData);
    } else {
      setPageGroupData([]);
    }
  }, [onlineUsers]);

  return (
    <div className="w-full p-8">
      {dataLoading ? (
        <LinearProgress />
      ) : (
        <>
          <div className="text-center mb-2 text-sm font-bold text-error">
            * ข้อมูลบน Dashboard อาจมีการ Delay ประมาณ 1 นาที *
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <Typography variant="subtitle2" className="font-bold">
                Work Overview
              </Typography>
            </div>
            <div className="col-span-3">
              <div className="flex items-center justify-between gap-4">
                <Typography variant="subtitle2" className="font-bold">
                  Role Overview
                </Typography>
                <div className="flex gap-2">
                  <Select
                    displayEmpty
                    onChange={(e) => {
                      onChangeChannel(e.target.value);
                    }}
                    size="small"
                    value={selectedChannel}
                    renderValue={(selected) => {
                      if (selected === "") {
                        return (
                          <div className="text-text-disabled">All Channels</div>
                        );
                      }

                      const selectedValue = channelFilterList.find(
                        (tt: { value: string }) => tt.value === selected,
                      );

                      if (selectedValue) {
                        return selectedValue.label;
                      }

                      return null;
                    }}
                  >
                    <MenuItem value="">
                      <em>All Channels</em>
                    </MenuItem>
                    {channelFilterList?.map(
                      (tt: { label: string; value: string }, index: number) => (
                        <MenuItem value={tt.value} key={`task-type-${index}`}>
                          {tt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isTask}
                          onChange={(e) => setIsTask(e.target.checked)}
                          color="info"
                        />
                      }
                      label={`${isTask ? "Task" : "Job"} Overview`}
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
            <div className="col-span-1">
              <TotalTaskToday
                isTask={isTask}
                data={data}
                titleOverview={titleOverview}
              />
              <UrgentTask
                isTask={isTask}
                data={data}
                titleOverview={titleOverview}
              />
              <TopTaskCreateReject data={data} />
            </div>
            <div className="col-span-1">
              <TaskCreator
                isTask={isTask}
                titleOverview={titleOverview}
                data={data}
              />
              {/* <Called isTask={isTask} data={data} /> */}
              {!onlineUsersLoading && pageGroupData && (
                <UserCalled isTask={isTask} data={pageGroupData} />
              )}

              <KeyIn isTask={isTask} data={data} />
            </div>
            <div className="col-span-1">
              <Support
                isTask={isTask}
                titleOverview={titleOverview}
                data={data}
              />
              <SupportResult isTask={isTask} data={data} />
            </div>
            <div className="col-span-1">
              <ToQCAnswer isTask={isTask} data={data} />
              <TopKeyinReject data={data} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
